import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';

import {
  EEventsType,
  EPublisherEvents,
  EBlockType,
  COMPONENT_BLOCKS,
  SINGLE_COLUMN_BLOCKS,
} from './enums';
import { enrichOptions } from './eventDataUtils';
import { trackMixpanelEvent } from './mixpanelClient';
import { BlockType, IBlock, IBlockItem } from './types';

interface HandleBlockClickParams {
  item: IBlockItem;
  blockType: BlockType;
  pathname: string;
  currentPageName?: string;
  platform?: string;
  playerId?: string;
  trackPublisherUIEvent: {
    mutate: (data: any) => void;
    mutateAsync?: (data: any) => Promise<unknown>;
  };
}

/**
 * Tracks a block click. Returns a promise that settles once the publisher
 * event request does (never rejects), so a click handler that navigates away
 * can wait for it; callers that don't navigate can ignore it.
 */
export const handleBlockClick = ({
  item,
  blockType,
  pathname,
  currentPageName,
  platform,
  playerId,
  trackPublisherUIEvent,
}: HandleBlockClickParams): Promise<void> | void => {
  const resolvedBlockType = normalizeBlockTypeForEvent(blockType);
  const isComponentType = isComponentBlock(resolvedBlockType);
  const itemWithoutType = _omit(item, 'type');
  const hasItemData = !isComponentType && !_isEmpty(itemWithoutType);

  const blockData: { type: BlockType; items?: IBlockItem[] } = {
    type: resolvedBlockType,
    ...(hasItemData && { items: [item] }),
  };

  trackMixpanelEvent(EEventsType.PORTAL_BLOCK_CLICKED, {
    block: blockData,
    'sessionMetaData.pageUrl': pathname,
    'sessionMetaData.pageName': currentPageName || '',
    'sessionMetaData.appVersion': process.env.NEXT_PUBLIC_APP_VERSION,
    player_id: playerId,
    device_platform: platform || '',
  });

  const publisherEvent = {
    ...enrichOptions({
      platform: platform || '',
      eventName: EPublisherEvents.PORTAL_BLOCK_CLICKED,
      block: blockData,
      page: [
        {
          pageUrl: pathname,
          pageName: currentPageName || '',
        },
      ],
      playerId: playerId,
    }),
  };

  if (!trackPublisherUIEvent.mutateAsync) {
    trackPublisherUIEvent.mutate(publisherEvent);
    return;
  }

  // The mutation's own onError still logs a failure; swallow the rejection here.
  return trackPublisherUIEvent.mutateAsync(publisherEvent).then(
    () => undefined,
    () => undefined
  );
};

export const normalizeBlockTypeForEvent = (blockType: BlockType): BlockType => {
  if (blockType === EBlockType.ARTICLE || blockType === EBlockType.NEWS) {
    return EBlockType.CONTENTS_PREVIEW;
  }
  return blockType;
};

export const isComponentBlock = (blockType: BlockType): boolean => {
  return (COMPONENT_BLOCKS as readonly string[]).includes(blockType);
};

export const isSingleColumnBlock = (blockType: BlockType): boolean => {
  return (SINGLE_COLUMN_BLOCKS as readonly string[]).includes(blockType);
};

export const shouldNotSplit = (blockType: BlockType): boolean => {
  return isComponentBlock(blockType) || isSingleColumnBlock(blockType);
};

export const splitBlock = (block: IBlock): IBlock[] => {
  const { items, itemsPerRow, type, title } = block;

  // Component blocks and single-column blocks don't split
  if (shouldNotSplit(type)) {
    return [block];
  }

  // Item-based blocks need items to split
  if (!items || items.length === 0) {
    return [block];
  }

  const actualItemsPerRow = itemsPerRow || items.length;
  const chunks: IBlock[] = [];
  for (let i = 0; i < items.length; i += actualItemsPerRow) {
    chunks.push({
      ...block,
      items: items.slice(i, i + actualItemsPerRow),
      title: i === 0 ? title : undefined,
    });
  }

  return chunks;
};

/** Determines if block items should be centered based on block type. */
export const shouldCenterBlockItems = (blockType: BlockType): boolean => {
  return (
    blockType === 'cta' ||
    blockType === 'image' ||
    blockType === 'video' ||
    blockType === 'progressBar'
  );
};

/** Determines if block items should be displayed in a column layout based on block type. */
export const shouldUseColumnLayout = (blockType: BlockType): boolean => {
  return blockType === 'dailyBonus' || blockType === 'progressBar';
};

const POST_BLOCKS: readonly BlockType[] = [
  EBlockType.NEWS,
  EBlockType.ARTICLE,
  EBlockType.CONTENTS_PREVIEW,
];

/**
 * Index of the block whose first card is the page's likely largest paint: the
 * first post block with items. Only that card's thumbnail is worth fetching at
 * high priority; the first card of every later block is usually below the fold
 * and would only take bandwidth from it.
 */
export const findPrimaryPostBlockIndex = (blocks: IBlock[]): number =>
  blocks.findIndex(
    (block: IBlock) => POST_BLOCKS.includes(block.type) && (block.items?.length ?? 0) > 0
  );
